/**
 * TEMPO & PITCH — two real calculators wired to real DSP.
 *
 * Built on the same terms as TrimPanel, and now with the same ending: the arithmetic is a
 * frontend job done correctly, and the audio processing is a backend job done for real.
 *
 * WHAT IS REAL IN THE PANEL. The slider and field are bound to each other, the BPM is
 * validated against a range, and the readouts are computed: 74 → 92 BPM really is 24% faster
 * and really would make a 4:05 track 3:17, and +7 semitones really is a frequency ratio of
 * 1.498. All of it is unit tested in lib/tempoPitch.ts.
 *
 * WHAT HAPPENS ON APPLY. `onApplyTempo`/`onApplyPitch` (hooks/useStudio) POST to
 * `/songs/:id/studio/tempo` and `/songs/:id/studio/pitch`, where the server time-stretches
 * (ffmpeg `atempo`, pitch preserved) or pitch-shifts (ffmpeg `asetrate`/`aresample`/`atempo`,
 * duration preserved) the song's stored master with real DSP, stores a new master and a new
 * version audio asset, and appends a Version History row — exactly as trim does. Neither
 * touches the generation provider: Lyria 3 takes no audio input, so this was never a
 * generation request; it is local signal processing over the file the song already has.
 *
 * WHAT IS WRITTEN TO THE SONG. Tempo updates the song's `length` and `bpm` to match the
 * stretched file; pitch leaves both alone because a pitch shift preserves duration. The
 * predicted duration shown beside the slider is what the server actually produces.
 *
 * See api/audioEditApi.ts — capabilities `tempo` and `pitch`, and the two request bodies
 * this panel's values fill in.
 */
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Gauge } from 'lucide-react';
import { Btn, Chip, PrototypeNotice, Tooltip } from '../ui';
import DrawerHeader from './DrawerHeader';
import {
  AUDIO_EDIT_CAPABILITIES,
  audioEditApi,
  hasStoredAudioFile,
  needsStoredAudioFinalization,
  storedAudioRequirementMessage,
} from '../../api/audioEditApi';
import { formatTime, parseLength } from '../../lib/formatting';
import {
  MAX_BPM,
  MAX_SEMITONES,
  MIN_BPM,
  clampSemitones,
  describeSemitones,
  describeTempoChange,
  resolveTempo,
  semitoneRatio,
} from '../../lib/tempoPitch';
import { c } from '../../styles/theme';
import type { Song, SongVersion } from '../../types/domain';

export interface TempoPitchPanelProps {
  song: Song;
  processing: boolean;
  onBack: () => void;
  onClose: () => void;
  onApplyTempo: (bpm: number) => void;
  onApplyPitch: (semitones: number) => void;
  /** The version explicitly loaded (Version History's "Load") as the source for the next edit. */
  editingHeadVersion?: SongVersion | null;
}

const LABEL: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 8px' };
const DESC: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 14px' };
const READOUT: CSSProperties = {
  fontSize: 11.5,
  fontFamily: 'IBM Plex Mono',
  color: c.text2,
  margin: '0 0 16px',
  minHeight: 16,
};

/**
 * The tempo presets, as ratios of the track's own BPM.
 *
 * Ratios rather than absolute figures, for the reason TrimPanel's presets are fractions:
 * "half speed" means something on a 74 BPM track and on a 140 BPM one, whereas a "70 BPM"
 * preset is a slowdown on one and a speed-up on the other.
 */
const TEMPO_PRESETS: Array<{ label: string; ratio: number }> = [
  { label: 'Half speed', ratio: 0.5 },
  { label: 'Slower', ratio: 0.9 },
  { label: 'Original', ratio: 1 },
  { label: 'Faster', ratio: 1.1 },
  { label: 'Double speed', ratio: 2 },
];

/** The two shifts anyone actually reaches for, plus the octaves and unison. */
const PITCH_PRESETS: Array<{ label: string; semitones: number }> = [
  { label: 'Octave down', semitones: -12 },
  { label: '5th down', semitones: -7 },
  { label: 'Original', semitones: 0 },
  { label: '5th up', semitones: 7 },
  { label: 'Octave up', semitones: 12 },
];

export default function TempoPitchPanel({
  song,
  processing,
  onBack,
  onClose,
  onApplyTempo,
  onApplyPitch,
  editingHeadVersion = null,
}: TempoPitchPanelProps) {
  const duration = parseLength(song.length);
  const fromBpm = song.bpm;

  /*
    Local state, not studioStore, for TrimPanel's reason exactly: nothing outside this
    panel reads a tempo or a pitch, no charged request is built from them, and a global
    field named after a capability that does not exist would be state nobody can use.
  */
  const [bpmText, setBpmText] = useState(() => String(fromBpm));
  const [semitones, setSemitones] = useState(0);
  const [activeAction, setActiveAction] = useState<'tempo' | 'pitch' | null>(null);

  useEffect(() => {
    if (!processing) setActiveAction(null);
  }, [processing]);

  const tempo = resolveTempo(bpmText, fromBpm, duration);
  const tempoCapability = AUDIO_EDIT_CAPABILITIES.tempo;
  const pitchCapability = AUDIO_EDIT_CAPABILITIES.pitch;
  const storedAudioActionSource = editingHeadVersion ?? song;
  const storedAudioStatusSource = {
    songOrigin: song.songOrigin,
    audioUrl: storedAudioActionSource.audioUrl,
  };
  const storedAudioReady = hasStoredAudioFile(storedAudioActionSource);
  const finalizingAudio = !storedAudioReady && needsStoredAudioFinalization(storedAudioStatusSource);
  // True once the backend exposes the studio tempo/pitch routes. Read through the API rather
  // than hardcoded, so if a deployment has no audio backend these buttons disable themselves
  // and the notice at the bottom explains why. See audioEditApi.supports.
  const canTempo = audioEditApi.supports('tempo');
  const canPitch = audioEditApi.supports('pitch');

  /** The submit control both halves share: present whether available or not. */
  function submit(input: {
    action: 'tempo' | 'pitch';
    idleLabel: string;
    processingLabel: string;
    capabilityAvailable: boolean;
    ready: boolean;
    unavailableReason: string;
    unavailableLabel: string;
    storedAudioAction: string;
    onClick: () => void;
  }) {
    const label = processing && activeAction === input.action ? input.processingLabel : input.idleLabel;
    const blockedByCapability = !input.capabilityAvailable;
    const blockedByStoredAudio = !blockedByCapability && !storedAudioReady;
    const enabled = !blockedByCapability && !blockedByStoredAudio && input.ready;
    const disabledLabel = blockedByCapability
      ? input.unavailableLabel
      : blockedByStoredAudio
        ? finalizingAudio
          ? 'finalizing audio'
          : 'stored audio required'
        : input.unavailableLabel;
    const disabledReason = blockedByCapability
      ? input.unavailableReason
      : blockedByStoredAudio
        ? storedAudioRequirementMessage(storedAudioStatusSource, input.storedAudioAction)
        : input.unavailableReason;
    const button = (
      <Btn
        full
        disabled={!enabled || processing}
        onClick={
          enabled && !processing
            ? () => {
                setActiveAction(input.action);
                input.onClick();
              }
            : undefined
        }
        aria-label={enabled ? label : `${input.idleLabel} — ${disabledLabel}`}
      >
        <Gauge size={13} />
        {label}
      </Btn>
    );
    if (enabled) return button;
    return (
      // A span inside the tooltip because a disabled button fires no pointer events —
      // without it the bubble would never open on the control that most needs to explain
      // why it cannot be pressed.
      <Tooltip label={disabledReason}>
        <span style={{ display: 'block', width: '100%' }}>{button}</span>
      </Tooltip>
    );
  }

  return (
    <div className="lyx-tool-panel">
      <DrawerHeader title="Tempo & Pitch" onBack={onBack} onClose={onClose} />
      <p style={DESC}>
        Change the tempo and key of &quot;{song.title}&quot;. The figures below are computed live,
        and applying either one processes the audio and saves it as a new version.
      </p>

      {editingHeadVersion && (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>
          Editing from {editingHeadVersion.label}. New edits will branch from this loaded head until another version is explicitly loaded.
        </PrototypeNotice>
      )}

      {/* ================= TEMPO ================= */}
      <p style={LABEL}>Tempo</p>

      {/*
        Slider and number field, bound to one value. Both, not one: dragging is how you
        explore and typing is how you hit 128 exactly, and a slider alone cannot be read
        precisely enough to know what you asked for.
      */}
      {/* The row's flex is .lyx-tool-row rather than an inline style, because it has to
          wrap on a phone: a 68px field plus "BPM" leaves the fader about 250px at 380px,
          and an inline `display: flex` cannot be given `flex-wrap` at a breakpoint. */}
      <div className="lyx-tool-row" style={{ marginBottom: 8 }}>
        <input
          id="studio-tempo-slider"
          type="range"
          min={MIN_BPM}
          max={MAX_BPM}
          step={1}
          // The slider cannot express an invalid value, so when the field holds one it
          // falls back to the song's own tempo rather than jumping to the minimum.
          value={tempo.bpm ?? fromBpm}
          onChange={(e) => setBpmText(e.target.value)}
          // Distinct from the field's label below rather than sharing it: two controls with
          // one accessible name are two things a screen reader cannot tell apart, and they
          // do genuinely differ — one drags, one is typed into.
          aria-label="Tempo slider"
          // The thumb position is not information a screen reader can read, so the
          // computed change is attached as the value's text.
          aria-valuetext={tempo.ratio ? describeTempoChange(tempo.ratio) : undefined}
          // No inline `flex: 1, minWidth: 0` — both live on .lyx-tool-slider now. An
          // inline flex-basis outranks the media query that gives a wrapped slider the
          // whole line, so the row above never actually wrapped. See that rule.
          className="lyx-tool-slider"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <label htmlFor="studio-tempo-bpm" className="lyx-visually-hidden">
            Tempo in BPM
          </label>
          <input
            id="studio-tempo-bpm"
            value={bpmText}
            onChange={(e) => setBpmText(e.target.value)}
            inputMode="numeric"
            aria-invalid={tempo.error !== null || undefined}
            aria-describedby={tempo.error ? 'studio-tempo-readout' : undefined}
            className="lyx-tool-field"
            style={{
              width: 68,
              background: c.surface2,
              border: `1px solid ${tempo.error ? c.danger : c.border}`,
              borderRadius: 8,
              padding: '8px 10px',
              color: c.text1,
              fontFamily: 'IBM Plex Mono',
              textAlign: 'center',
            }}
          />
          <span style={{ fontSize: 11, color: c.text2 }}>BPM</span>
        </div>
      </div>

      {/*
        One live region for the tempo half, always present so it is not a node appearing
        and disappearing from the accessibility tree. Holds the error when there is one and
        the computed result when there is not. `polite` because it changes on every drag.
      */}
      <p id="studio-tempo-readout" role="status" aria-live="polite" style={READOUT}>
        {tempo.error ??
          (tempo.ratio
            ? `${describeTempoChange(tempo.ratio)} · was ${fromBpm} BPM` +
              (tempo.seconds !== null
                ? ` · would run ${formatTime(tempo.seconds)} instead of ${formatTime(duration)}`
                : '')
            : '')}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {TEMPO_PRESETS.map((preset) => {
          const target = Math.round(fromBpm * preset.ratio);
          return (
            <Chip
              key={preset.label}
              label={preset.label}
              // Active when the field already describes this preset, so the chips read as
              // a selection rather than as five one-way buttons.
              active={tempo.bpm === target}
              onClick={() => setBpmText(String(target))}
            />
          );
        })}
      </div>

      {submit({
        action: 'tempo',
        idleLabel: 'Apply tempo',
        processingLabel: 'Applying tempo…',
        capabilityAvailable: canTempo,
        ready: tempo.bpm !== null && tempo.bpm !== fromBpm,
        unavailableLabel: canTempo ? 'no change to apply' : 'not available yet',
        unavailableReason: !canTempo
          ? 'Not available yet — time stretching needs an audio service.'
          : (tempo.error ?? 'Enter a different tempo to apply a change.'),
        storedAudioAction: 'change this song\'s tempo',
        onClick: () => {
          if (tempo.bpm !== null) onApplyTempo(tempo.bpm);
        },
      })}

      {/* ================= PITCH ================= */}
      <p style={{ ...LABEL, marginTop: 22 }}>Pitch</p>

      <div className="lyx-tool-row" style={{ marginBottom: 8 }}>
        <input
          id="studio-pitch-slider"
          type="range"
          min={-MAX_SEMITONES}
          max={MAX_SEMITONES}
          step={1}
          value={semitones}
          onChange={(e) => setSemitones(clampSemitones(Number(e.target.value)))}
          aria-label="Pitch in semitones"
          aria-valuetext={describeSemitones(semitones)}
          className="lyx-tool-slider"
        />
        <span
          style={{
            fontSize: 12,
            fontFamily: 'IBM Plex Mono',
            color: c.text1,
            width: 40,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          {/* U+2212 minus rather than a hyphen, matching the rest of the app's numbers. */}
          {semitones > 0 ? `+${semitones}` : semitones < 0 ? `−${Math.abs(semitones)}` : '0'}
        </span>
      </div>

      <p id="studio-pitch-readout" role="status" aria-live="polite" style={READOUT}>
        {describeSemitones(semitones)}
        {semitones !== 0 && ` · frequency ×${semitoneRatio(semitones).toFixed(3)} · length unchanged`}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {PITCH_PRESETS.map((preset) => (
          <Chip
            key={preset.label}
            label={preset.label}
            active={semitones === preset.semitones}
            onClick={() => setSemitones(preset.semitones)}
          />
        ))}
      </div>

      {submit({
        action: 'pitch',
        idleLabel: 'Apply pitch',
        processingLabel: 'Applying pitch…',
        capabilityAvailable: canPitch,
        ready: semitones !== 0,
        unavailableLabel: canPitch ? 'no shift selected' : 'not available yet',
        unavailableReason: !canPitch
          ? 'Not available yet — pitch shifting needs an audio service.'
          : 'Move the slider off 0 to apply a shift.',
        storedAudioAction: 'change this song\'s pitch',
        onClick: () => onApplyPitch(semitones),
      })}

      {!canTempo && !canPitch ? (
        <PrototypeNotice style={{ marginTop: 16 }}>
          {/* Only shown when neither route is available — a deployment with no audio backend.
              The missing pieces come from the one place that records them, so this copy cannot
              drift from what audioEditApi reports. When the routes are live (the normal case)
              both changes apply for real and this notice does not render. */}
          Tempo and pitch changes need an audio service that is not configured here. Tempo:{' '}
          {tempoCapability.missing} Pitch: {pitchCapability.missing}
        </PrototypeNotice>
      ) : !storedAudioReady ? (
        <PrototypeNotice style={{ marginTop: 16 }}>
          {storedAudioRequirementMessage(storedAudioStatusSource, 'change this song\'s tempo or pitch')}
        </PrototypeNotice>
      ) : null}
    </div>
  );
}
